import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getProject, getRecordings, getDocuments, getProjectDocuments, getWafCafAuditStats, getTranscript, downloadTranscript, Recording } from '../services/api'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

export default function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>()

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: recordings } = useQuery({
    queryKey: ['recordings', projectId],
    queryFn: () => getRecordings(projectId).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: documents } = useQuery({
    queryKey: ['documents', projectId],
    queryFn: () => getDocuments(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: sourceDocuments } = useQuery({
    queryKey: ['projectDocuments', projectId],
    queryFn: () => getProjectDocuments(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: auditStats } = useQuery({
    queryKey: ['wafCafAuditStats', projectId],
    queryFn: () => getWafCafAuditStats(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  // Transcript view state
  const [showTranscriptModal, setShowTranscriptModal] = useState(false)
  const [viewingTranscript, setViewingTranscript] = useState<{ sections: Array<{ start_time: number; speaker?: string; text: string }> } | null>(null)
  const [transcriptRecording, setTranscriptRecording] = useState<Recording | null>(null)

  const handleViewTranscript = async (recording: Recording) => {
    try {
      const response = await getTranscript(recording.id)
      setViewingTranscript(response.data)
      setTranscriptRecording(recording)
      setShowTranscriptModal(true)
    } catch (error) {
      console.error('Failed to load transcript:', error)
    }
  }

  const handleDownloadTranscript = async (recording: Recording) => {
    try {
      await downloadTranscript(recording.id, `${recording.original_filename || 'transcript'}.txt`)
    } catch (error) {
      console.error('Failed to download transcript:', error)
    }
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '--:--'
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#60a5fa' }}></i>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading project...</p>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(100, 116, 139, 0.2)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
          <i className="fa-solid fa-folder-open text-3xl" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
        </div>
        <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>Project not found</h3>
        <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>The project you're looking for doesn't exist</p>
        <Link
          to="/projects"
          className="px-6 py-3 rounded-lg text-sm font-medium transition inline-flex items-center space-x-2"
          style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
        >
          <i className="fa-solid fa-arrow-left"></i>
          <span>Back to Projects</span>
        </Link>
      </div>
    )
  }

  const transcribedCount =
    recordings?.filter((r) => r.status === 'transcribed' || r.status === 'ready').length || 0
  const completedDocs = documents?.filter((d) => d.status === 'completed').length || 0
  const indexedDocs = sourceDocuments?.filter((d) => d.status === 'indexed').length || 0

  const auditCount = auditStats?.audit_count || 0
  const auditChunks = auditStats?.chunk_count || 0

  const sections = [
    {
      title: 'Recordings & Transcripts',
      description: 'Upload and manage workshop recordings',
      icon: 'fa-microphone',
      color: '#34d399',
      bgColor: 'rgba(52, 211, 153, 0.15)',
      borderColor: 'rgba(52, 211, 153, 0.3)',
      href: `/projects/${projectId}/recordings`,
      stats: `${recordings?.length || 0} recordings, ${transcribedCount} transcribed`,
      step: 1,
    },
    {
      title: 'Source Documents',
      description: 'Upload customer docs, drawings, notes',
      icon: 'fa-folder-open',
      color: '#f97316',
      bgColor: 'rgba(249, 115, 22, 0.15)',
      borderColor: 'rgba(249, 115, 22, 0.3)',
      href: `/projects/${projectId}/source-documents`,
      stats: `${sourceDocuments?.length || 0} documents, ${indexedDocs} indexed`,
      step: 2,
    },
    {
      title: 'WAF-CAF Audit',
      description: 'Upload Azure environment audit data',
      icon: 'fa-shield-halved',
      color: '#14b8a6',
      bgColor: 'rgba(20, 184, 166, 0.15)',
      borderColor: 'rgba(20, 184, 166, 0.3)',
      href: `/projects/${projectId}/waf-caf-audit`,
      stats: `${auditCount} audits, ${auditChunks} chunks indexed`,
      step: 3,
    },
    {
      title: 'Audit Reports',
      description: 'View audit data in tables',
      icon: 'fa-table',
      color: '#06b6d4',
      bgColor: 'rgba(6, 182, 212, 0.15)',
      borderColor: 'rgba(6, 182, 212, 0.3)',
      href: `/projects/${projectId}/audit-reports`,
      stats: auditCount > 0 ? `${auditCount} audits ready` : 'Upload audits first',
      step: 4,
    },
    {
      title: 'Chat with Data',
      description: 'Ask questions about transcripts, documents & audits',
      icon: 'fa-robot',
      color: '#60a5fa',
      bgColor: 'rgba(96, 165, 250, 0.15)',
      borderColor: 'rgba(96, 165, 250, 0.3)',
      href: `/projects/${projectId}/query`,
      stats: (transcribedCount > 0 || indexedDocs > 0 || auditChunks > 0) ? 'Ready to chat' : 'Add content first',
      step: 5,
    },
    {
      title: 'Generated Documents',
      description: 'Generate documentation',
      icon: 'fa-file-alt',
      color: '#a78bfa',
      bgColor: 'rgba(167, 139, 250, 0.15)',
      borderColor: 'rgba(167, 139, 250, 0.3)',
      href: `/projects/${projectId}/documents`,
      stats: `${completedDocs} documents generated`,
      step: 6,
    },
  ]

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
      case 'transcribed':
        return (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full flex items-center"
            style={{ background: 'rgba(52, 211, 153, 0.2)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
            <i className="fa-solid fa-check-circle mr-1"></i>
            Transcribed
          </span>
        )
      case 'transcribing':
      case 'processing':
        return (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full flex items-center"
            style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
            <i className="fa-solid fa-spinner fa-spin mr-1"></i>
            Processing
          </span>
        )
      case 'error':
        return (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full flex items-center"
            style={{ background: 'rgba(248, 113, 113, 0.2)', color: '#f87171', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
            <i className="fa-solid fa-times-circle mr-1"></i>
            Error
          </span>
        )
      default:
        return (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full flex items-center"
            style={{ background: 'rgba(148, 163, 184, 0.2)', color: 'rgba(148, 163, 184, 0.8)', border: '1px solid rgba(148, 163, 184, 0.3)' }}>
            <i className="fa-solid fa-circle text-[8px] mr-1"></i>
            Uploaded
          </span>
        )
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/projects"
          className="inline-flex items-center text-sm font-medium mb-4 transition hover:opacity-80"
          style={{ color: '#60a5fa' }}
        >
          <i className="fa-solid fa-arrow-left mr-2"></i>
          Back to Projects
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>{project.project_name}</h1>
            <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>{project.customer_name}</p>
            {project.description && <p className="mt-2" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>{project.description}</p>}
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-3 px-4 py-2 rounded-lg"
              style={{ background: 'rgba(167, 139, 250, 0.1)', border: '1px solid rgba(167, 139, 250, 0.2)' }}>
              <i className="fa-solid fa-microphone" style={{ color: '#a78bfa' }}></i>
              <div className="text-left">
                <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Recordings</p>
                <p className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>{recordings?.length || 0}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 px-4 py-2 rounded-lg"
              style={{ background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.2)' }}>
              <i className="fa-solid fa-file-alt" style={{ color: '#60a5fa' }}></i>
              <div className="text-left">
                <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Documents</p>
                <p className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>{completedDocs}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sections.map((section) => (
          <Link
            key={section.title}
            to={section.href}
            className="rounded-xl p-6 transition-all duration-300 group hover:scale-[1.02]"
            style={{ ...glassPanel, borderColor: section.borderColor }}
          >
            <div className="flex items-center justify-between mb-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ background: section.bgColor, border: `1px solid ${section.borderColor}` }}
              >
                <i className={`fa-solid ${section.icon} text-2xl`} style={{ color: section.color }}></i>
              </div>
              <div className="flex items-center">
                <span className="text-xs font-semibold px-3 py-1 rounded-full"
                  style={{ background: 'rgba(100, 116, 139, 0.3)', color: 'rgba(148, 163, 184, 0.8)' }}>
                  Step {section.step}
                </span>
              </div>
            </div>
            <h3 className="font-bold text-lg mb-1 transition" style={{ color: '#e2e8f0' }}>
              {section.title}
            </h3>
            <p className="text-sm mb-3" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>{section.description}</p>
            <p className="text-xs flex items-center" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              <i className="fa-solid fa-info-circle mr-1"></i>
              {section.stats}
            </p>
          </Link>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="rounded-xl p-6" style={glassPanel}>
        <h2 className="text-lg font-bold mb-4" style={{ color: '#e2e8f0' }}>Recent Activity</h2>
        {recordings && recordings.length > 0 ? (
          <div className="space-y-3">
            {recordings.slice(0, 5).map((recording) => (
              <div
                key={recording.id}
                className="flex items-center justify-between p-4 rounded-xl transition"
                style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.05)' }}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(167, 139, 250, 0.15)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                    <i className="fa-solid fa-microphone" style={{ color: '#a78bfa' }}></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{recording.original_filename}</p>
                    <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                      {new Date(recording.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {(recording.status === 'transcribed' || recording.status === 'ready') && (
                    <>
                      <button
                        onClick={() => handleViewTranscript(recording)}
                        title="View transcript"
                        className="p-2 rounded-lg transition"
                        style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                        onMouseOver={(e) => { e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)' }}
                        onMouseOut={(e) => { e.currentTarget.style.color = 'rgba(148, 163, 184, 0.6)'; e.currentTarget.style.background = 'transparent' }}
                      >
                        <i className="fa-solid fa-eye"></i>
                      </button>
                      <button
                        onClick={() => handleDownloadTranscript(recording)}
                        title="Download transcript"
                        className="p-2 rounded-lg transition"
                        style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                        onMouseOver={(e) => { e.currentTarget.style.color = '#34d399'; e.currentTarget.style.background = 'rgba(52, 211, 153, 0.1)' }}
                        onMouseOut={(e) => { e.currentTarget.style.color = 'rgba(148, 163, 184, 0.6)'; e.currentTarget.style.background = 'transparent' }}
                      >
                        <i className="fa-solid fa-download"></i>
                      </button>
                    </>
                  )}
                  {getStatusBadge(recording.status)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(100, 116, 139, 0.2)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <i className="fa-solid fa-microphone text-xl" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
            </div>
            <p className="mb-4" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>No recordings yet</p>
            <Link
              to={`/projects/${projectId}/recordings`}
              className="px-4 py-2 rounded-lg text-sm font-medium transition inline-flex items-center space-x-2"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
            >
              <i className="fa-solid fa-upload"></i>
              <span>Upload your first recording</span>
            </Link>
          </div>
        )}
      </div>

      {/* Tips */}
      {recordings && recordings.length === 0 && (
        <div className="rounded-xl p-8"
          style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(100, 180, 255, 0.3)', boxShadow: '0 0 20px rgba(59, 130, 246, 0.2)' }}>
              <i className="fa-solid fa-lightbulb text-xl" style={{ color: '#fbbf24' }}></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2" style={{ color: '#e2e8f0' }}>Getting Started</h3>
              <p className="text-sm mb-4" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                Follow these steps to generate your documentation:
              </p>
              <ul className="space-y-2 text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                <li className="flex items-start space-x-2">
                  <i className="fa-solid fa-check-circle mt-1" style={{ color: '#34d399' }}></i>
                  <span>
                    <strong style={{ color: '#e2e8f0' }}>Upload recordings</strong> - Add your workshop audio or video files
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="fa-solid fa-check-circle mt-1" style={{ color: '#34d399' }}></i>
                  <span>
                    <strong style={{ color: '#e2e8f0' }}>Transcribe</strong> - Our AI will convert your recordings to text
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="fa-solid fa-check-circle mt-1" style={{ color: '#34d399' }}></i>
                  <span>
                    <strong style={{ color: '#e2e8f0' }}>Query</strong> - Ask questions about the workshop content
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="fa-solid fa-check-circle mt-1" style={{ color: '#34d399' }}></i>
                  <span>
                    <strong style={{ color: '#e2e8f0' }}>Generate</strong> - Create professional documents
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Transcript View Modal */}
      {showTranscriptModal && viewingTranscript && transcriptRecording && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
            <div className="p-6" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(167, 139, 250, 0.15)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                    <i className="fa-solid fa-file-lines" style={{ color: '#a78bfa' }}></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>Transcript</h2>
                    <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>{transcriptRecording.original_filename}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDownloadTranscript(transcriptRecording)}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center space-x-2"
                    style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
                  >
                    <i className="fa-solid fa-download"></i>
                    <span>Download</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowTranscriptModal(false)
                      setViewingTranscript(null)
                      setTranscriptRecording(null)
                    }}
                    className="p-2 rounded-lg transition"
                    style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                    onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(100, 116, 139, 0.3)' }}
                    onMouseOut={(e) => { e.currentTarget.style.background = 'transparent' }}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="space-y-4">
                {viewingTranscript.sections.map((section, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="flex-shrink-0 w-16 text-sm font-mono" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                      {formatDuration(section.start_time)}
                    </div>
                    <div className="flex-1">
                      {section.speaker && (
                        <div className="text-sm font-medium mb-1" style={{ color: '#60a5fa' }}>
                          {section.speaker}
                        </div>
                      )}
                      <p style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{section.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
